import * as React from "react";
import styled from "styled-components";
import { Button, FormGroup } from "@lib/components";
import {
	FLOOR_STATUS_COLORS,
	filterTablesByFloor,
	groupTablesByArea,
	resolveTableTemplateId,
	type FloorTableLiveState,
} from "@lib/common";
import { TableTemplateGraphic } from "@lib/components";
import { FaMinus, FaPlus, FaRedo } from "react-icons/fa";
import {
	clampTablePosition,
	collectExplicitJoinLinks,
	collectJoinGroups,
	DEFAULT_FLOOR_GRID,
	formatJoinGroupLabel,
	joinGroupBoundsPct,
	effectiveCanvasSize,
	ensureTableLayouts,
	FLOOR_TABLE_ROTATION_STEP,
	floorTableDisplayRatio,
	FLOOR_ZOOM_DEFAULT,
	FLOOR_ZOOM_MAX,
	FLOOR_ZOOM_MIN,
	FLOOR_ZOOM_STEP,
	rotateTable,
	snapCoord,
	tableCenter,
	tableLayoutDimensions,
	tableLayoutRotation,
	tableSupportsRotation,
	toggleJoinLink,
} from "./dine-in-floor-plan.helpers";

type Table = T.Schema.Restaurant.Services.DineInTable;

interface Props {
	tables: Table[];
	grid?: number;
	floorPlanId?: string;
	readOnly?: boolean;
	liveByTableId?: Record<string, FloorTableLiveState>;
	onChange?: (tables: Table[]) => void;
}

interface DragState {
	tableId: string;
	offsetX: number;
	offsetY: number;
}

interface PanState {
	startX: number;
	startY: number;
	scrollLeft: number;
	scrollTop: number;
}

interface State {
	selectedId: string | null;
	selectedAreaId: string | null;
	zoom: number;
}

const Wrap = styled.div``;

const CanvasOuter = styled.div`
	margin-top: 0.75rem;
	max-width: 920px;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	overflow: hidden;
	background: #fff;
`;

const ZoomToolbar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 8px 12px;
	border-bottom: 1px solid #e2e8f0;
	background: #fff;
`;

const ZoomToolbarHint = styled.span`
	font-size: 11px;
	color: #94a3b8;
`;

const ZoomToolbarActions = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
`;

const ZoomLabel = styled.span`
	min-width: 44px;
	text-align: center;
	font-size: 12px;
	font-weight: 700;
	color: #475569;
`;

const CanvasViewport = styled.div<{ $canPan?: boolean }>`
	width: 100%;
	aspect-ratio: 10 / 7;
	overflow: ${(p) => (p.$canPan ? "auto" : "hidden")};
	background: #f8fafc;
	cursor: ${(p) => (p.$canPan ? "grab" : "default")};

	&:active {
		cursor: ${(p) => (p.$canPan ? "grabbing" : "default")};
	}
`;

const CanvasStage = styled.div<{ $zoom: number; $zoomedOut?: boolean }>`
	position: relative;
	width: ${(p) => (p.$zoomedOut ? "100%" : `${p.$zoom * 100}%`)};
	aspect-ratio: 10 / 7;
	min-height: ${(p) => (p.$zoomedOut ? "auto" : "100%")};
	background: #f8fafc;
	touch-action: none;
	user-select: none;
	--floor-zoom: ${(p) => p.$zoom};
	overflow: hidden;
`;

const CanvasContent = styled.div<{ $zoom: number; $zoomedOut?: boolean }>`
	position: relative;
	width: ${(p) => (p.$zoomedOut ? `${100 / p.$zoom}%` : "100%")};
	aspect-ratio: 10 / 7;
	transform: ${(p) => (p.$zoomedOut ? `scale(${p.$zoom})` : "none")};
	transform-origin: top left;
	background: #f8fafc;
	background-image:
		linear-gradient(to right, rgba(148, 163, 184, 0.12) 1px, transparent 1px),
		linear-gradient(to bottom, rgba(148, 163, 184, 0.12) 1px, transparent 1px);
	background-size: calc(100% / 20) calc(100% / 14);
`;

const AreaTabBar = styled.div`
	display: flex;
	align-items: stretch;
	gap: 0;
	border-top: 1px solid #e2e8f0;
	background: #f1f5f9;
	overflow-x: auto;
`;

const AreaTab = styled.button<{ $active?: boolean }>`
	flex: 1 0 auto;
	min-width: 0;
	padding: 10px 16px;
	border: none;
	border-right: 1px solid #e2e8f0;
	background: ${(p) => (p.$active ? "#fff" : "transparent")};
	color: ${(p) => (p.$active ? "#fb2f2f" : "#64748b")};
	font-size: 13px;
	font-weight: ${(p) => (p.$active ? 700 : 600)};
	cursor: pointer;
	box-shadow: ${(p) =>
		p.$active ? "inset 0 2px 0 #fb2f2f" : "none"};
	white-space: nowrap;

	&:last-child {
		border-right: none;
	}

	&:hover {
		color: ${(p) => (p.$active ? "#fb2f2f" : "#334155")};
		background: ${(p) => (p.$active ? "#fff" : "#e2e8f0")};
	}
`;

const AreaTabCount = styled.span`
	margin-left: 6px;
	font-size: 11px;
	font-weight: 600;
	color: inherit;
	opacity: 0.75;
`;

const TableNode = styled.div<{
	$selected?: boolean;
	$readOnly?: boolean;
}>`
	position: absolute;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 2px;
	padding: 0;
	border: none;
	background: transparent;
	box-shadow: none;
	cursor: ${(p) => (p.$readOnly ? "default" : "grab")};
	text-align: center;
	overflow: visible;
	z-index: 1;

	&:active {
		cursor: ${(p) => (p.$readOnly ? "default" : "grabbing")};
	}
`;

const TableGraphicWrap = styled.div<{ $selected?: boolean; $rotation?: number }>`
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	isolation: isolate;
	outline: ${(p) => (p.$selected ? "2px solid #fb2f2f" : "none")};
	outline-offset: 2px;
	border-radius: 8px;
	transform: rotate(${(p) => p.$rotation || 0}deg);
	transform-origin: center center;
`;

const RotateToolbar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 8px 12px;
	border-top: 1px solid #e2e8f0;
	background: #fff;
`;

const RotateToolbarLeft = styled.div`
	font-size: 12px;
	font-weight: 600;
	color: #475569;
`;

const RotateToolbarActions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const TableLabel = styled.div<{ $color?: string; $bg?: string; $scaleText?: boolean }>`
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	padding: ${(p) =>
		p.$scaleText
			? "calc(2px * var(--floor-zoom, 1)) calc(6px * var(--floor-zoom, 1))"
			: "2px 6px"};
	border-radius: 4px;
	background: ${(p) => p.$bg || "rgba(255,255,255,0.94)"};
	font-size: ${(p) =>
		p.$scaleText ? "calc(12px * var(--floor-zoom, 1))" : "12px"};
	font-weight: 800;
	color: ${(p) => p.$color || "#0f172a"};
	line-height: 1.2;
	max-width: 92%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	pointer-events: none;
`;

const TableStatus = styled.div<{ $color?: string; $bg?: string; $scaleText?: boolean }>`
	position: absolute;
	left: 50%;
	bottom: ${(p) => (p.$scaleText ? "calc(4px * var(--floor-zoom, 1))" : "4px")};
	transform: translateX(-50%);
	padding: ${(p) =>
		p.$scaleText
			? "calc(2px * var(--floor-zoom, 1)) calc(6px * var(--floor-zoom, 1))"
			: "2px 6px"};
	border-radius: 4px;
	background: ${(p) => p.$bg || "rgba(255,255,255,0.94)"};
	font-size: ${(p) =>
		p.$scaleText ? "calc(10px * var(--floor-zoom, 1))" : "10px"};
	font-weight: 700;
	color: ${(p) => p.$color || "#64748b"};
	letter-spacing: 0.02em;
	white-space: nowrap;
	pointer-events: none;
`;

const Hint = styled.p`
	margin: 0.5rem 0 0;
	font-size: 12px;
	color: #64748b;
	line-height: 1.45;
`;

const JoinSvg = styled.svg`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	z-index: 0;
`;

const MergedGroupHull = styled.div<{ $border?: string; $bg?: string }>`
	position: absolute;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border: 2px solid ${(p) => p.$border || "#fb2f2f"};
	background: ${(p) => p.$bg || "rgba(251, 47, 47, 0.06)"};
	border-radius: 14px;
	box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.65);
	pointer-events: none;
	z-index: 0;
`;

const MergedGroupLabel = styled.div<{ $color?: string; $bg?: string; $scaleText?: boolean }>`
	padding: ${(p) =>
		p.$scaleText
			? "calc(3px * var(--floor-zoom, 1)) calc(8px * var(--floor-zoom, 1))"
			: "3px 8px"};
	border-radius: 6px;
	background: ${(p) => p.$bg || "rgba(255,255,255,0.94)"};
	font-size: ${(p) =>
		p.$scaleText ? "calc(12px * var(--floor-zoom, 1))" : "12px"};
	font-weight: 800;
	color: ${(p) => p.$color || "#0f172a"};
	line-height: 1.2;
	max-width: 92%;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	text-align: center;
`;

const MergedGroupStatus = styled.div<{ $color?: string; $bg?: string; $scaleText?: boolean }>`
	margin-top: ${(p) => (p.$scaleText ? "calc(4px * var(--floor-zoom, 1))" : "4px")};
	padding: ${(p) =>
		p.$scaleText
			? "calc(2px * var(--floor-zoom, 1)) calc(6px * var(--floor-zoom, 1))"
			: "2px 6px"};
	border-radius: 4px;
	background: ${(p) => p.$bg || "rgba(255,255,255,0.94)"};
	font-size: ${(p) =>
		p.$scaleText ? "calc(10px * var(--floor-zoom, 1))" : "10px"};
	font-weight: 700;
	color: ${(p) => p.$color || "#64748b"};
	letter-spacing: 0.02em;
	white-space: nowrap;
`;

const LIVE_STATUS_PRIORITY: FloorTableLiveState["status"][] = [
	"needs_order",
	"ready",
	"seated",
	"confirmed",
	"unconfirmed",
	"reserved",
	"open",
];

function resolveJoinGroupLiveState(
	groupIds: string[],
	liveByTableId?: Record<string, FloorTableLiveState>
): FloorTableLiveState | null {
	if (!liveByTableId) return null;
	let best: FloorTableLiveState | null = null;
	let bestRank = LIVE_STATUS_PRIORITY.length;
	for (const id of groupIds) {
		const live = liveByTableId[id];
		if (!live) continue;
		const rank = LIVE_STATUS_PRIORITY.indexOf(live.status);
		if (rank >= 0 && rank < bestRank) {
			bestRank = rank;
			best = live;
		}
	}
	return best;
}

export class DineInFloorPlanEditor extends React.Component<Props, State> {
	private viewportRef = React.createRef<HTMLDivElement>();
	private canvasRef = React.createRef<HTMLDivElement>();
	private dragRef: DragState | null = null;
	private panRef: PanState | null = null;

	state: State = {
		selectedId: null,
		selectedAreaId: null,
		zoom: FLOOR_ZOOM_DEFAULT,
	};

	componentDidMount() {
		this.ensureLayoutsOnce();
		this.syncSelectedArea();
	}

	componentDidUpdate(prevProps: Props) {
		if (prevProps.tables !== this.props.tables) {
			const added = this.props.tables.some(
				(t) => !prevProps.tables.find((p) => p._id === t._id)
			);
			if (added) {
				this.ensureLayoutsOnce();
			}
		}
		if (
			prevProps.tables !== this.props.tables ||
			prevProps.floorPlanId !== this.props.floorPlanId
		) {
			this.syncSelectedArea();
		}
	}

	syncSelectedArea = () => {
		const groups = this.areaGroups();
		if (!groups.length) {
			if (this.state.selectedAreaId !== null) {
				this.setState({ selectedAreaId: null, selectedId: null });
			}
			return;
		}
		const current = this.state.selectedAreaId;
		if (!current || !groups.some((g) => g.id === current)) {
			this.setState({ selectedAreaId: groups[0].id, selectedId: null });
		}
	};

	componentWillUnmount() {
		this.detachDragListeners();
		this.detachPanListeners();
	}

	ensureLayoutsOnce = () => {
		const { tables, onChange, readOnly } = this.props;
		if (readOnly || !onChange) return;
		const { tables: next, changed } = ensureTableLayouts(tables);
		if (changed) onChange(next);
	};

	gridSize = () => this.props.grid || DEFAULT_FLOOR_GRID;

	updateTableLayout = (tableId: string, x: number, y: number) => {
		const grid = this.gridSize();
		const canvasSize = effectiveCanvasSize(this.state.zoom);
		const next = this.props.tables.map((t) => {
			if (t._id !== tableId) return t;
			const { w, h } = tableLayoutDimensions(t);
			const snapped = clampTablePosition(
				snapCoord(x, grid),
				snapCoord(y, grid),
				w,
				h,
				canvasSize
			);
			return {
				...t,
				layout: {
					...(t.layout || {}),
					w,
					h,
					x: snapped.x,
					y: snapped.y,
				},
			};
		});
		this.props.onChange?.(next);
	};

	onPointerDown = (e: React.PointerEvent, table: Table) => {
		if (this.props.readOnly) return;
		if (e.button !== 0) return;
		if (e.shiftKey && this.state.selectedId && this.state.selectedId !== table._id) {
			this.props.onChange?.(
				toggleJoinLink(this.props.tables, this.state.selectedId, table._id)
			);
			e.preventDefault();
			return;
		}
		const canvas = this.canvasRef.current;
		if (!canvas) return;
		const rect = canvas.getBoundingClientRect();
		const canvasSize = effectiveCanvasSize(this.state.zoom);
		const lx = table.layout?.x ?? 0;
		const ly = table.layout?.y ?? 0;
		const tableLeftPx = (lx / canvasSize) * rect.width;
		const tableTopPx = (ly / canvasSize) * rect.height;
		this.dragRef = {
			tableId: table._id,
			offsetX: e.clientX - rect.left - tableLeftPx,
			offsetY: e.clientY - rect.top - tableTopPx,
		};
		this.setState({ selectedId: table._id });
		(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
		window.addEventListener("pointermove", this.onWindowPointerMove);
		window.addEventListener("pointerup", this.onWindowPointerUp);
		window.addEventListener("pointercancel", this.onWindowPointerUp);
		e.preventDefault();
	};

	onWindowPointerMove = (e: PointerEvent) => {
		const drag = this.dragRef;
		const canvas = this.canvasRef.current;
		if (!drag || !canvas) return;
		const rect = canvas.getBoundingClientRect();
		const canvasSize = effectiveCanvasSize(this.state.zoom);
		const leftPx = e.clientX - rect.left - drag.offsetX;
		const topPx = e.clientY - rect.top - drag.offsetY;
		this.updateTableLayout(
			drag.tableId,
			(leftPx / rect.width) * canvasSize,
			(topPx / rect.height) * canvasSize
		);
	};

	onWindowPointerUp = () => {
		this.dragRef = null;
		this.detachDragListeners();
	};

	detachDragListeners = () => {
		window.removeEventListener("pointermove", this.onWindowPointerMove);
		window.removeEventListener("pointerup", this.onWindowPointerUp);
		window.removeEventListener("pointercancel", this.onWindowPointerUp);
	};

	detachPanListeners = () => {
		window.removeEventListener("pointermove", this.onWindowPanMove);
		window.removeEventListener("pointerup", this.onWindowPanUp);
		window.removeEventListener("pointercancel", this.onWindowPanUp);
	};

	canPanViewport = () => this.state.zoom > 1.01;

	clampZoom = (zoom: number) =>
		Math.min(FLOOR_ZOOM_MAX, Math.max(FLOOR_ZOOM_MIN, zoom));

	changeZoom = (delta: number) => {
		this.setState((s) => ({
			zoom: this.clampZoom(
				Math.round((s.zoom + delta) * 100) / 100
			),
		}));
	};

	resetZoom = () => {
		const viewport = this.viewportRef.current;
		this.setState({ zoom: FLOOR_ZOOM_DEFAULT }, () => {
			if (viewport) {
				viewport.scrollLeft = 0;
				viewport.scrollTop = 0;
			}
		});
	};

	onStagePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		if (e.button !== 0) return;
		if (e.target !== this.canvasRef.current) return;
		if (!this.canPanViewport()) return;
		const viewport = this.viewportRef.current;
		if (!viewport) return;
		this.panRef = {
			startX: e.clientX,
			startY: e.clientY,
			scrollLeft: viewport.scrollLeft,
			scrollTop: viewport.scrollTop,
		};
		this.setState({ selectedId: null });
		(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
		window.addEventListener("pointermove", this.onWindowPanMove);
		window.addEventListener("pointerup", this.onWindowPanUp);
		window.addEventListener("pointercancel", this.onWindowPanUp);
		e.preventDefault();
	};

	onWindowPanMove = (e: PointerEvent) => {
		const pan = this.panRef;
		const viewport = this.viewportRef.current;
		if (!pan || !viewport) return;
		viewport.scrollLeft = pan.scrollLeft - (e.clientX - pan.startX);
		viewport.scrollTop = pan.scrollTop - (e.clientY - pan.startY);
	};

	onWindowPanUp = () => {
		this.panRef = null;
		this.detachPanListeners();
	};

	floorTables = () => {
		const { tables, floorPlanId } = this.props;
		if (!floorPlanId) return tables;
		return filterTablesByFloor(tables, floorPlanId);
	};

	areaGroups = () => groupTablesByArea(this.floorTables());

	activeAreaId = () => {
		const groups = this.areaGroups();
		if (!groups.length) return null;
		const { selectedAreaId } = this.state;
		if (selectedAreaId && groups.some((g) => g.id === selectedAreaId)) {
			return selectedAreaId;
		}
		return groups[0].id;
	};

	displayTables = () => {
		const groups = this.areaGroups();
		const activeId = this.activeAreaId();
		if (!activeId) return [];
		return groups.find((g) => g.id === activeId)?.tables || [];
	};

	selectedTable = () => {
		const { selectedId } = this.state;
		if (!selectedId) return null;
		return this.props.tables.find((t) => t._id === selectedId) || null;
	};

	rotateSelectedTable = (deltaDeg: number) => {
		const selected = this.selectedTable();
		if (!selected || !this.props.onChange) return;
		this.props.onChange(
			rotateTable(this.props.tables, selected._id, deltaDeg)
		);
	};

	render() {
		const { readOnly, liveByTableId } = this.props;
		const floorTables = this.floorTables();
		const areaGroups = this.areaGroups();
		const tables = this.displayTables();
		const { selectedId } = this.state;
		const activeAreaId = this.activeAreaId();
		const selectedTable = this.selectedTable();
		const showRotateToolbar =
			!readOnly &&
			selectedTable &&
			tableSupportsRotation(selectedTable);
		const { zoom } = this.state;
		const zoomedOut = zoom < 0.999;
		const canPan = this.canPanViewport();
		const zoomPct = Math.round(zoom * 100);
		const canvasSize = effectiveCanvasSize(zoom);
		const tableSizeRatio = floorTableDisplayRatio(zoom);
		const joinGroups = readOnly ? collectJoinGroups(tables) : [];
		const mergedTableIds = new Set(joinGroups.flat());

		if (!floorTables.length) {
			return (
				<FormGroup title="Floor plan" help="Add tables on the Tables tab first, then place them here.">
					<Hint>No tables configured yet.</Hint>
				</FormGroup>
			);
		}

		return (
			<Wrap>
				<FormGroup
					title="Floor plan"
					help={
						readOnly
							? "Live table status from bookings and dine-in orders."
							: "Drag tables to match your dining room. Adjacent tables can be joined for large parties; Shift+click a second table to add or remove an explicit join link."
					}
				>
					<CanvasOuter>
						<ZoomToolbar>
							<ZoomToolbarHint>
								{canPan
									? "Scroll or drag empty space to pan"
									: zoomedOut
										? "Drag tables into the extended floor area"
										: "Zoom out (−) for a larger floor area"}
							</ZoomToolbarHint>
							<ZoomToolbarActions>
								<Button
									type="button"
									size="xs"
									color="white"
									disabled={zoom <= FLOOR_ZOOM_MIN}
									onClick={() =>
										this.changeZoom(-FLOOR_ZOOM_STEP)
									}
									title="Zoom out"
								>
									<FaMinus />
								</Button>
								<ZoomLabel>{zoomPct}%</ZoomLabel>
								<Button
									type="button"
									size="xs"
									color="white"
									disabled={zoom >= FLOOR_ZOOM_MAX}
									onClick={() =>
										this.changeZoom(FLOOR_ZOOM_STEP)
									}
									title="Zoom in"
								>
									<FaPlus />
								</Button>
								<Button
									type="button"
									size="xs"
									color="white"
									disabled={zoom === FLOOR_ZOOM_DEFAULT}
									onClick={this.resetZoom}
								>
									Reset
								</Button>
							</ZoomToolbarActions>
						</ZoomToolbar>
						<CanvasViewport ref={this.viewportRef} $canPan={canPan}>
							<CanvasStage
								$zoom={zoom}
								$zoomedOut={zoomedOut}
								onPointerDown={this.onStagePointerDown}
							>
							<CanvasContent
								ref={this.canvasRef}
								$zoom={zoom}
								$zoomedOut={zoomedOut}
							>
							<JoinSvg aria-hidden>
								{!readOnly &&
									collectExplicitJoinLinks(tables).map(([aId, bId]) => {
										const a = tables.find((t) => t._id === aId);
										const b = tables.find((t) => t._id === bId);
										if (!a || !b) return null;
										const ac = tableCenter(a);
										const bc = tableCenter(b);
										return (
											<line
												key={`${aId}-${bId}`}
												x1={`${(ac.x / canvasSize) * 100}%`}
												y1={`${(ac.y / canvasSize) * 100}%`}
												x2={`${(bc.x / canvasSize) * 100}%`}
												y2={`${(bc.y / canvasSize) * 100}%`}
												stroke="#fb2f2f"
												strokeWidth={2}
												strokeDasharray="6 4"
											/>
										);
									})}
							</JoinSvg>
							{readOnly &&
								joinGroups.map((groupIds) => {
									const bounds = joinGroupBoundsPct(
										groupIds,
										tables,
										canvasSize
									);
									if (!bounds) return null;
									const groupLabel = formatJoinGroupLabel(
										groupIds,
										tables
									);
									const groupLive = resolveJoinGroupLiveState(
										groupIds,
										liveByTableId
									);
									const colors = groupLive
										? FLOOR_STATUS_COLORS[groupLive.status]
										: {
												bg: "rgba(251, 47, 47, 0.06)",
												border: "#fb2f2f",
												text: "#414651",
										  };
									const hullBg =
										groupLive && groupLive.status !== "open"
											? colors.bg
											: "rgba(251, 47, 47, 0.06)";
									return (
										<MergedGroupHull
											key={[...groupIds].sort().join("-")}
											$border={colors.border}
											$bg={hullBg}
											style={{
												left: `${bounds.leftPct}%`,
												top: `${bounds.topPct}%`,
												width: `${bounds.widthPct}%`,
												height: `${bounds.heightPct}%`,
											}}
										>
											<MergedGroupLabel
												title={groupLabel}
												$color={colors.text}
												$bg={
													groupLive
														? colors.bg
														: "rgba(255,255,255,0.94)"
												}
												$scaleText={!zoomedOut}
											>
												{groupLabel}
											</MergedGroupLabel>
											{groupLive && groupLive.status !== "open" && (
												<MergedGroupStatus
													$color={colors.text}
													$bg={colors.bg}
													$scaleText={!zoomedOut}
												>
													{groupLive.label}
												</MergedGroupStatus>
											)}
										</MergedGroupHull>
									);
								})}
							{tables.map((t, idx) => {
								const lx = t.layout?.x ?? 0;
								const ly = t.layout?.y ?? 0;
								const label = t.name?.trim() || `Table ${idx + 1}`;
								const inMergedGroup = mergedTableIds.has(t._id);
								const live = liveByTableId?.[t._id];
								const colors = live
									? FLOOR_STATUS_COLORS[live.status]
									: null;
								const templateId = resolveTableTemplateId(t);
								const rotation = tableLayoutRotation(t);
								const labelRotate =
									rotation !== 0
										? `translate(-50%, -50%) rotate(${-rotation}deg)`
										: undefined;
								return (
									<TableNode
										key={t._id}
										$selected={selectedId === t._id}
										$readOnly={readOnly}
										style={{
											left: `${(lx / canvasSize) * 100}%`,
											top: `${(ly / canvasSize) * 100}%`,
											width: `${tableSizeRatio * 100}%`,
											height: `${tableSizeRatio * 100}%`,
										}}
										onPointerDown={(e) => this.onPointerDown(e, t)}
									>
										<TableGraphicWrap
											$selected={selectedId === t._id}
											$rotation={rotation}
										>
											<TableTemplateGraphic
												templateId={templateId}
												floorCanvas
												fill
											/>
										</TableGraphicWrap>
										{!(readOnly && inMergedGroup) && (
											<TableLabel
												title={label}
												$color={colors?.text}
												$bg={colors?.bg}
												$scaleText={!zoomedOut}
												style={
													labelRotate
														? { transform: labelRotate }
														: undefined
												}
											>
												{label}
											</TableLabel>
										)}
										{live && !(readOnly && inMergedGroup) && (
											<TableStatus
												$color={colors?.text}
												$bg={colors?.bg}
												$scaleText={!zoomedOut}
												style={
													labelRotate
														? {
																transform: `translateX(-50%) rotate(${-rotation}deg)`,
														  }
														: undefined
												}
											>
												{live.label}
											</TableStatus>
										)}
									</TableNode>
								);
							})}
							{!tables.length && (
								<Hint style={{ padding: "2rem 1rem", textAlign: "center" }}>
									No tables in this area.
								</Hint>
							)}
							</CanvasContent>
							</CanvasStage>
						</CanvasViewport>
						{showRotateToolbar && selectedTable && (
							<RotateToolbar>
								<RotateToolbarLeft>
									{selectedTable.name?.trim() || "Table"} ·{" "}
									{tableLayoutRotation(selectedTable)}°
								</RotateToolbarLeft>
								<RotateToolbarActions>
									<Button
										type="button"
										size="xs"
										color="white"
										onClick={() =>
											this.rotateSelectedTable(
												-FLOOR_TABLE_ROTATION_STEP
											)
										}
									>
										↺ −{FLOOR_TABLE_ROTATION_STEP}°
									</Button>
									<Button
										type="button"
										size="xs"
										color="primary-inverse"
										onClick={() =>
											this.rotateSelectedTable(
												FLOOR_TABLE_ROTATION_STEP
											)
										}
									>
										<FaRedo style={{ marginRight: 4 }} />
										Rotate +{FLOOR_TABLE_ROTATION_STEP}°
									</Button>
								</RotateToolbarActions>
							</RotateToolbar>
						)}
						<AreaTabBar>
							{areaGroups.map((group) => (
								<AreaTab
									key={group.id}
									type="button"
									$active={activeAreaId === group.id}
									onClick={() =>
										this.setState({
											selectedAreaId: group.id,
											selectedId: null,
										})
									}
								>
									{group.label}
									<AreaTabCount>{group.tables.length}</AreaTabCount>
								</AreaTab>
							))}
						</AreaTabBar>
					</CanvasOuter>
					{!readOnly && (
						<Hint>
							Tip: use zoom to fit large layouts; scroll or drag the canvas to pan when zoomed.
							Shift+click joins tables. Rectangle tables rotate in 15° steps — save to persist.
						</Hint>
					)}
				</FormGroup>
			</Wrap>
		);
	}
}
